/**
 * DetectedTestOrdersPanel — surfaces likely-test orders in the catalog
 * and lets the admin review + selectively clean them up.
 *
 * Workflow (mandatory preview before destructive action):
 *   1. Mount → GET /detect-test-orders (read-only scan)
 *   2. Admin ticks rows → "Preview cleanup selected"
 *      → POST /preview-cleanup, shows linked transactions / messages /
 *        files / money impact / affected wallets
 *   3. Admin types "CLEAN TEST DATA" → "Run cleanup"
 *      → POST /cleanup-orders with confirm_text, response shows what
 *        was actually deleted + wallet before/after.
 *
 * Detection rules live entirely on the backend (`_classify_order` in
 * `routes/admin.py`).  This UI is a viewport — it has no opinions of
 * its own about which orders are "test".
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Trash2, Shield, AlertTriangle, Eye, CheckSquare, Square, Filter,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtEUR = (n) => new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

async function api(token, method, path, body) {
  const r = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j?.detail || `HTTP ${r.status}`);
  }
  return r.json();
}

const CONF_META = {
  high:   { color: 'bg-rose-600 text-white',   label: 'High'   },
  medium: { color: 'bg-amber-500 text-white',  label: 'Medium' },
  low:    { color: 'bg-slate-400 text-white',  label: 'Low'    },
};

export default function DetectedTestOrdersPanel({ token, onCleanupApplied }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, rows: [], by_confidence: {}, scanned: 0 });
  const [filterConf, setFilterConf] = useState('all');     // all | high | medium | low
  const [filterText, setFilterText] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [recompute, setRecompute] = useState(true);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setSelected(new Set()); setPreview(null); setLastResult(null);
    try {
      const d = await api(token, 'GET', '/api/admin/maintenance/detect-test-orders?limit=500');
      setData(d);
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Filtered rows for display — filter is purely visual, selection
  // survives across filter changes so the admin can pick across pages.
  const visibleRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (filterConf !== 'all' && r.confidence !== filterConf) return false;
      if (!q) return true;
      return (r.order_number || '').toLowerCase().includes(q)
          || (r.user_email   || '').toLowerCase().includes(q)
          || (r.model        || '').toLowerCase().includes(q)
          || (r.manufacturer || '').toLowerCase().includes(q);
    });
  }, [data.rows, filterConf, filterText]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPreview(null);
  };

  const selectAll = (rows) => {
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.order_id));
      return next;
    });
    setPreview(null);
  };

  const selectHighConfidence = () => selectAll(data.rows.filter((r) => r.confidence === 'high'));
  const selectCancelled      = () => selectAll(data.rows.filter((r) => (r.status || '').toLowerCase() === 'cancelled'));
  const clearSelection       = () => { setSelected(new Set()); setPreview(null); };

  const runPreview = async () => {
    if (selected.size === 0) { toast.error('Select at least one order'); return; }
    setPreviewing(true); setPreview(null); setConfirmText(''); setLastResult(null);
    try {
      const p = await api(token, 'POST', '/api/admin/maintenance/preview-cleanup', {
        order_ids: Array.from(selected),
      });
      setPreview(p);
    } catch (e) { toast.error(e.message || 'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const runCleanup = async () => {
    if (!preview) return;
    if (confirmText !== 'CLEAN TEST DATA') {
      toast.error('Type CLEAN TEST DATA to confirm');
      return;
    }
    setRunning(true);
    try {
      const r = await api(token, 'POST', '/api/admin/maintenance/cleanup-orders', {
        order_ids: Array.from(selected),
        confirm_text: 'CLEAN TEST DATA',
        recompute_balances: recompute,
      });
      setLastResult(r);
      const totalDeleted = Object.values(r.deleted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Cleaned ${totalDeleted} records`, { description: `${r.users_recomputed?.length || 0} wallet(s) recomputed` });
      onCleanupApplied && onCleanupApplied();
      // Auto-refresh the detect list so cleaned rows disappear.
      setTimeout(load, 400);
    } catch (e) { toast.error(e.message || 'Cleanup failed'); }
    finally { setRunning(false); }
  };

  const totalSelected = selected.size;

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="detected-test-orders-card">
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Shield className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-slate-800">Detected test orders</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Heuristic scan of recent orders. Select rows you want to clean — preview is mandatory before any delete.
              <br />
              Scanned <span className="font-mono">{fmt(data.scanned)}</span> · Detected{' '}
              <span className="font-mono font-bold">{fmt(data.total)}</span> ·{' '}
              <span className="font-mono text-rose-700">{fmt(data.by_confidence.high || 0)}</span> high ·{' '}
              <span className="font-mono text-amber-700">{fmt(data.by_confidence.medium || 0)}</span> medium ·{' '}
              <span className="font-mono text-slate-600">{fmt(data.by_confidence.low || 0)}</span> low.
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading} data-testid="detected-refresh"
                className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-bold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Re-scan
        </button>
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex bg-slate-100 rounded-lg p-0.5 text-[11px] font-bold">
          {['all', 'high', 'medium', 'low'].map((c) => (
            <button key={c} data-testid={`detected-conf-${c}`}
                    onClick={() => setFilterConf(c)}
                    className={`px-2.5 py-1 rounded ${filterConf === c ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
              {c === 'all' ? 'All' : CONF_META[c]?.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="Filter by order # / email / vehicle…"
                 value={filterText} onChange={(e) => setFilterText(e.target.value)}
                 data-testid="detected-filter-text"
                 className="w-full h-9 pl-8 pr-2 text-sm rounded-lg border border-slate-200 focus:border-cyan-500 outline-none" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={selectHighConfidence} data-testid="detected-select-high"
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-rose-100 hover:bg-rose-200 text-rose-700">
            Select high
          </button>
          <button onClick={selectCancelled} data-testid="detected-select-cancelled"
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-800">
            Select cancelled
          </button>
          <button onClick={() => selectAll(visibleRows)} data-testid="detected-select-page"
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700">
            Select page
          </button>
          <button onClick={clearSelection} disabled={!totalSelected} data-testid="detected-clear-selection"
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-50">
            Clear ({totalSelected})
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-10 text-slate-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Scanning orders…
        </div>
      ) : data.total === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="detected-empty">
          <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-bold text-emerald-800">No suspect orders detected</p>
          <p className="text-xs text-emerald-700 mt-1">Recent orders look like real customer activity.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="px-2 py-2 w-8 whitespace-nowrap"></th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Conf.</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Order #</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Date</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Customer</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Vehicle</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">Amount</th>
                <th className="px-2 py-2 text-left whitespace-nowrap">Reasons</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, idx) => {
                const checked = selected.has(r.order_id);
                const conf = CONF_META[r.confidence] || CONF_META.low;
                return (
                  <tr key={r.order_id} data-testid={`detected-row-${idx}`}
                      className={`border-t border-slate-100 ${checked ? 'bg-rose-50' : 'hover:bg-slate-50'}`}>
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => toggleOne(r.order_id)}
                              data-testid={`detected-check-${idx}`}
                              className={`w-5 h-5 inline-flex items-center justify-center rounded ${checked ? 'text-rose-600' : 'text-slate-400 hover:text-slate-600'}`}>
                        {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${conf.color}`}>{conf.label}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-slate-700">{r.order_number || '—'}</td>
                    <td className="px-2 py-1.5 text-slate-500">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5 text-slate-700 truncate max-w-[180px]" title={r.user_email}>{r.user_email || '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        (r.status || '').toLowerCase() === 'cancelled' ? 'bg-rose-100 text-rose-700'
                        : (r.status || '').toLowerCase() === 'completed' ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-700'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[200px]" title={`${r.manufacturer || ''} ${r.model || ''}`.trim()}>
                      {[r.manufacturer, r.model].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-700">{fmtEUR(r.amount)}</td>
                    <td className="px-2 py-1.5 text-slate-500 max-w-[300px]">
                      <ul className="space-y-0.5">
                        {r.reasons.map((x, j) => <li key={j} className="truncate" title={x}>• {x}</li>)}
                      </ul>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview + Apply */}
      {totalSelected > 0 && (
        <div className="mt-4 bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-200 rounded-xl p-4" data-testid="detected-cleanup-area">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
              <h4 className="text-sm font-bold text-slate-800">
                {totalSelected} order{totalSelected === 1 ? '' : 's'} selected for cleanup
              </h4>
            </div>
            <div className="flex gap-2">
              <button onClick={runPreview} disabled={previewing || running} data-testid="detected-run-preview"
                      className="h-9 px-3 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                Preview cleanup
              </button>
            </div>
          </div>

          {preview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                <Pill label="Orders"        value={preview.found}                       tone="rose" />
                <Pill label="Transactions"  value={preview.linked.transactions}        tone="amber" />
                <Pill label="Messages"      value={preview.linked.messages}            tone="slate" />
                <Pill label="Files"         value={preview.linked.files}               tone="slate" />
                <Pill label="EVC purch."    value={preview.linked.evc_purchases}       tone="slate" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                <Pill label="Orders amount" value={fmtEUR(preview.orders_total_amount)} tone="rose" />
                <Pill label="Tx amount"     value={fmtEUR(preview.linked.transactions_amount)} tone="amber" />
                <Pill label="Customers"     value={preview.affected_users?.length || 0} tone="slate" />
              </div>
              {preview.affected_users?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-2 mb-3 text-[11px]">
                  <div className="font-bold text-slate-700 mb-1">Affected customers</div>
                  <ul className="space-y-0.5">
                    {preview.affected_users.map((u) => (
                      <li key={u.user_id} className="flex items-center justify-between gap-2">
                        <span className="text-slate-700 truncate">{u.email || u.user_id}</span>
                        <span className="font-mono text-slate-600">balance now {fmtEUR(u.current_balance)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {preview.warnings?.length > 0 && (
                <ul className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 text-[11px] text-amber-800 list-disc list-inside">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-0.5">
                    Type <span className="font-mono text-rose-700">CLEAN TEST DATA</span> to confirm
                  </label>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                         data-testid="detected-confirm-text"
                         placeholder="CLEAN TEST DATA"
                         className="w-full h-9 px-2 text-sm rounded-lg border border-slate-200 focus:border-rose-500 outline-none font-mono" />
                </div>
                <label className="flex items-center gap-1 px-3 h-9 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700">
                  <input type="checkbox" checked={recompute} onChange={(e) => setRecompute(e.target.checked)}
                         data-testid="detected-recompute"
                         className="accent-rose-600" />
                  Recompute wallets
                </label>
                <button onClick={runCleanup}
                        disabled={running || confirmText !== 'CLEAN TEST DATA'}
                        data-testid="detected-run-cleanup"
                        className="h-9 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center gap-1.5 disabled:bg-slate-300 disabled:cursor-not-allowed">
                  {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Run cleanup
                </button>
              </div>
            </>
          )}

          {lastResult && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px]" data-testid="detected-result">
              <div className="font-bold text-emerald-800 mb-1.5">Cleanup complete</div>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-emerald-700 mb-2">
                {Object.entries(lastResult.deleted || {}).map(([k, v]) => (
                  <li key={k}><span className="text-emerald-600 font-bold">{v}</span> {k}</li>
                ))}
              </ul>
              {lastResult.users_recomputed?.length > 0 && (
                <>
                  <div className="font-bold text-emerald-800 mb-0.5">Wallet recompute</div>
                  <ul className="space-y-0.5">
                    {lastResult.users_recomputed.map((u) => (
                      <li key={u.user_id} className="text-slate-700">
                        <span className="font-mono">{u.email}</span>: {fmtEUR(u.before)} → {fmtEUR(u.after)}
                        <span className={`ml-1 font-bold ${u.diff >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          ({u.diff >= 0 ? '+' : ''}{fmtEUR(u.diff)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Pill({ label, value, tone }) {
  const tones = {
    rose:  'bg-rose-100  text-rose-800  border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-base font-bold tabular-nums leading-tight">{value}</div>
    </div>
  );
}
